import { createAsyncThunk } from '@reduxjs/toolkit';
import { Enums, Paths, Value } from '@utils/constants';
import { addLoading, removeLoading } from '../appAction';
import { axios } from '@utils/lib';

export const getApiListAsync = createAsyncThunk(
  'api/getApiListAsync',
  async (query: IApiListQuery, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const searchQuery: Record<string, string> = {
      'pagination[page]': query.page ?? Value.TABLE_PAGE_INIT,
      'pagination[pageSize]': query.pageSize ?? Value.TABLE_PAGE_SIZE,
      sort: 'id:desc',
      populate: 'apiCategory',
    };
    if (query.name) searchQuery['filters[name][$contains]'] = query.name;
    if (query.apiCategory)
      searchQuery['filters[apiCategory][$eq]'] = query.apiCategory;

    const params = new URLSearchParams(searchQuery).toString();
    try {
      const { data } = await axios.request<IListRes<IApiRes>>(
        Enums.HTTP_METHOD.GET,
        Paths.APIS + '?' + params,
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);

export const registerApiAsync = createAsyncThunk(
  'api/registerApiAsync',
  async (body: IApiForm, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request(Enums.HTTP_METHOD.POST, Paths.APIS, {
        ...body,
        apiCategory: Number(body.apiCategory),
        dataSchema: JSON.parse(JSON.stringify(body.dataSchema)),
      });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);

export const updateApiAsync = createAsyncThunk(
  'api/updateApiAsync',
  async (body: IApiForm, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const { id, apiCategory } = body;
    try {
      const { data } = await axios.request(
        Enums.HTTP_METHOD.PUT,
        Paths.APIS + `/${id}`,
        {
          ...body,
          apiCategory: Number(apiCategory),
        },
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);

export const getApiDetailsAsync = createAsyncThunk(
  'api/getApiDetailsAsync',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request<IDataRes<IApiRes>>(
        Enums.HTTP_METHOD.GET,
        Paths.APIS + `/${id}?populate=apiCategory`,
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);

export const registerCategoryAsync = createAsyncThunk(
  'api/registerCategoryAsync',
  async (body: IAddApiCategoryName, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request(
        Enums.HTTP_METHOD.POST,
        Paths.API_CATEGORY,
        body,
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);

export const getCategoryListAsync = createAsyncThunk(
  'api/getCategoryListAsync',
  async (
    { query, hideLoading }: GetCategoryListQuery,
    { rejectWithValue, dispatch },
  ) => {
    const searchQuery: Record<string, string> = {
      'pagination[page]': query.page ?? Value.TABLE_PAGE_INIT,
      'pagination[pageSize]': query.pageSize ?? Value.TABLE_PAGE_SIZE,
      sort: 'displayOrder:desc',
    };
    if (query.name) searchQuery['filters[name][$contains]'] = query.name;
    const params = new URLSearchParams(searchQuery).toString();
    if (!hideLoading) {
      dispatch(addLoading());
    }
    try {
      const { data } = await axios.request<IListRes<ICategoryData>>(
        Enums.HTTP_METHOD.GET,
        Paths.API_CATEGORY + '?' + params,
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  },
);
